/**
 * useWallet
 *
 * Composable for Ethereum wallet connection using AppKit (Web3Modal v3).
 * Provides connect, disconnect, and wallet state management.
 */

import { ref, computed, onMounted, onUnmounted } from 'vue';
import { mainnet } from 'viem/chains';

// Get project ID from environment
const projectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID || '';

// Lazy-load AppKit only when needed (avoids import issues)
let appKit: any = null;
let appKitInitPromise: Promise<any> | null = null;

async function initAppKit() {
  if (appKit) return appKit;
  if (appKitInitPromise) return appKitInitPromise;

  appKitInitPromise = (async () => {
    try {
      const [{ createAppKit }, { WagmiAdapter }] = await Promise.all([
        import('@reown/appkit'),
        import('@reown/appkit-adapter-wagmi')
      ]);

      const metadata = {
        name: 'MEGA.ARMY',
        description: 'Real-time blockchain visualization',
        url: typeof window !== 'undefined' ? window.location.origin : 'https://mega.army',
        icons: ['https://mega.army/icon.png']
      };

      // Use viem's mainnet chain
      const networks = [mainnet] as const;

      const wagmiAdapter = new WagmiAdapter({
        networks: networks as any,
        projectId
      });

      appKit = createAppKit({
        adapters: [wagmiAdapter],
        networks: networks as any,
        projectId,
        metadata,
        features: {
          analytics: false,
          email: false,
          socials: false
        }
      });

      return appKit;
    } catch (e) {
      console.error('Failed to initialize AppKit:', e);
      appKitInitPromise = null;
      return null;
    }
  })();

  return appKitInitPromise;
}

// Shared reactive state
const address = ref<string | null>(null);
const isConnecting = ref(false);
const error = ref<string | null>(null);

export function useWallet() {
  const isConnected = computed(() => !!address.value);
  const isConfigured = computed(() => !!projectId);

  const truncatedAddress = computed(() => {
    if (!address.value) return null;
    return `${address.value.slice(0, 6)}...${address.value.slice(-4)}`;
  });

  // Open the connection modal
  const connect = async () => {
    if (!projectId) {
      error.value = 'Wallet not configured. Missing VITE_WALLETCONNECT_PROJECT_ID.';
      console.error(error.value);
      return;
    }

    try {
      isConnecting.value = true;
      error.value = null;
      const kit = await initAppKit();
      if (kit) {
        kit.open();
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to open wallet modal';
      console.error('Wallet connect error:', e);
    } finally {
      isConnecting.value = false;
    }
  };

  // Disconnect the wallet
  const disconnect = async () => {
    const kit = await initAppKit();
    if (!kit) return;

    try {
      await kit.disconnect();
      address.value = null;
    } catch (e) {
      console.error('Wallet disconnect error:', e);
    }
  };

  // Track subscription for cleanup
  let unsubscribeAccount: (() => void) | null = null;

  // Initialize subscription to account changes
  onMounted(async () => {
    if (!projectId) {
      return;
    }

    const kit = await initAppKit();
    if (!kit) return;

    // Subscribe to account changes
    unsubscribeAccount = kit.subscribeAccount((account: { address?: string }) => {
      address.value = account.address ?? null;
      if (account.address) {
        // console.log('🔗 Wallet connected:', account.address);
      } else {
        // console.log('🔗 Wallet disconnected');
      }
    });
  });

  // Cleanup subscription on unmount
  onUnmounted(() => {
    if (unsubscribeAccount) {
      unsubscribeAccount();
    }
  });

  return {
    // State
    address,
    isConnected,
    isConnecting,
    isConfigured,
    truncatedAddress,
    error,
    // Actions
    connect,
    disconnect
  };
}
